
import React, { useState } from 'react';
import { UserProfile, GroceryProduct } from '../../../app/domain/types/index.ts';
import { Search, ShoppingCart, Plus, Minus, Filter, ArrowRight, ExternalLink, Leaf } from 'lucide-react';

interface GroceryIntegrationProps {
  user: UserProfile;
}

const GroceryIntegration: React.FC<GroceryIntegrationProps> = ({ user }) => {
  const [cart, setCart] = useState<{product: GroceryProduct, qty: number}[]>([]);
  const [activeCategory, setActiveCategory] = useState('All');

  // Mock Data
  const products: GroceryProduct[] = [
    { id: '1', name: 'Organic Bananas', price: 0.69, image: '🍌', brand: 'Whole Foods', size: '1 lb', healthScore: 'A', category: 'Produce' },
    { id: '2', name: 'Almond Milk', price: 3.99, image: '🥛', brand: 'Almond Breeze', size: '64 oz', healthScore: 'B', category: 'Dairy' },
    { id: '3', name: 'Quinoa', price: 5.49, image: '🥣', brand: 'Ancient Harvest', size: '12 oz', healthScore: 'A', category: 'Pantry' },
    { id: '4', name: 'Chicken Breast', price: 8.99, image: '🍗', brand: 'Local Farm', size: '1.5 lb', healthScore: 'A', category: 'Meat' },
    { id: '5', name: 'Greek Yogurt', price: 1.29, image: '🍦', brand: 'Chobani', size: '5.3 oz', healthScore: 'A', category: 'Dairy' },
    { id: '6', name: 'Spinach', price: 2.99, image: '🥬', brand: 'Fresh Express', size: '10 oz', healthScore: 'A', category: 'Produce' },
  ];

  const categories = ['All', 'Produce', 'Dairy', 'Meat', 'Pantry', 'Frozen'];

  const addToCart = (product: GroceryProduct) => {
    const existing = cart.find(item => item.product.id === product.id);
    if (existing) {
      setCart(cart.map(item => item.product.id === product.id ? { ...item, qty: item.qty + 1 } : item));
    } else {
      setCart([...cart, { product, qty: 1 }]);
    }
  };

  const removeFromCart = (productId: string) => {
    const existing = cart.find(item => item.product.id === productId);
    if (existing && existing.qty > 1) {
      setCart(cart.map(item => item.product.id === productId ? { ...item, qty: item.qty - 1 } : item));
    } else {
      setCart(cart.filter(item => item.product.id !== productId));
    }
  };

  const filteredProducts = activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
  const cartTotal = cart.reduce((sum, item) => sum + (item.product.price * item.qty), 0);

  return (
    <div className="h-full flex flex-col md:flex-row bg-[#FAFAFA] dark:bg-[#0f0f0f] relative">

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 md:p-8 pb-4 border-b border-stone-200 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md sticky top-0 z-20">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Grocery Market</h1>

          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search for groceries..."
                className="w-full pl-10 pr-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
                    activeCategory === cat 
                    ? 'bg-green-600 text-white border-green-600' 
                    : 'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-gray-600 dark:text-gray-300 hover:bg-stone-50 dark:hover:bg-stone-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 no-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredProducts.map(product => (
              <div key={product.id} className="bg-white dark:bg-stone-800 rounded-2xl p-4 border border-stone-100 dark:border-stone-700 shadow-sm hover:shadow-md transition-all group relative">
                <div className="absolute top-3 right-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-bold px-2 py-0.5 rounded border border-green-200 dark:border-green-800">
                  Score: {product.healthScore}
                </div>
                <div className="h-32 bg-stone-50 dark:bg-stone-700/50 rounded-xl flex items-center justify-center text-6xl mb-4 group-hover:scale-105 transition-transform">
                  {product.image}
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white">{product.name}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{product.brand} • {product.size}</p>
                <div className="flex justify-between items-center mt-3">
                  <span className="font-bold text-lg text-gray-900 dark:text-white">${product.price}</span>
                  <button
                    onClick={() => addToCart(product)}
                    className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-lg shadow-green-600/20"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cart Sidebar */}
      <div className="w-full md:w-96 bg-white dark:bg-stone-900 border-l border-stone-200 dark:border-stone-800 flex flex-col h-[40vh] md:h-full shadow-2xl md:shadow-none fixed bottom-0 md:relative z-30">
        <div className="p-6 border-b border-stone-100 dark:border-stone-800 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShoppingCart size={20} /> Your Cart
          </h2>
          <span className="bg-stone-100 dark:bg-stone-800 px-2 py-1 rounded text-xs font-bold text-gray-500 dark:text-gray-400">
            {cart.reduce((acc, item) => acc + item.qty, 0)} items
          </span>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {cart.length === 0 ? (
            <div className="text-center text-gray-400 mt-10">
              <Leaf size={48} className="mx-auto mb-3 opacity-20" />
              <p>Your cart is empty.</p>
              <p className="text-xs mt-1">Start adding healthy foods!</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.product.id} className="flex gap-3 items-center">
                <div className="w-12 h-12 bg-stone-50 dark:bg-stone-800 rounded-lg flex items-center justify-center text-xl">
                  {item.product.image}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm text-gray-900 dark:text-white">{item.product.name}</h4>
                  <p className="text-xs text-gray-500">${item.product.price}</p>
                </div>
                <div className="flex items-center gap-2 bg-stone-100 dark:bg-stone-800 rounded-lg p-1">
                  <button onClick={() => removeFromCart(item.product.id)} className="p-1 hover:bg-white dark:hover:bg-stone-700 rounded transition-colors"><Minus size={12} /></button>
                  <span className="text-xs font-bold w-4 text-center">{item.qty}</span>
                  <button onClick={() => addToCart(item.product)} className="p-1 hover:bg-white dark:hover:bg-stone-700 rounded transition-colors"><Plus size={12} /></button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-6 border-t border-stone-100 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50">
          <div className="flex justify-between mb-2 text-sm">
            <span className="text-gray-500">Subtotal</span>
            <span className="font-bold text-gray-900 dark:text-white">${cartTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between mb-4 text-sm">
            <span className="text-gray-500">Delivery</span>
            <span className="font-bold text-gray-900 dark:text-white">$5.99</span>
          </div>
          <div className="flex justify-between mb-6 text-lg font-bold">
            <span className="text-gray-900 dark:text-white">Total</span>
            <span className="text-green-600 dark:text-green-400">${(cartTotal + (cartTotal > 0 ? 5.99 : 0)).toFixed(2)}</span>
          </div>

          <button
            disabled={cart.length === 0}
            className="w-full py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] transition-transform"
          >
            Checkout <ArrowRight size={18} />
          </button>

          <div className="mt-4 flex justify-center gap-4">
            <div className="text-[10px] text-gray-400 flex items-center gap-1 uppercase font-bold">Powered by <span className="text-green-600">Instacart</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroceryIntegration;
